<template>
  <div
    class="adn-input"
    :class="{
      'adn-input--labelled': label,
    }"
    data-test-component
    @click="onInputClick"
    @focus.prevent="onInputFocus"
    @keyup.esc.prevent="hideOptions"
    @keyup.enter.prevent="onKeyUpEnter"
    @keydown.up.prevent="onKeyDownUp"
    @keydown.down.prevent="onKeyDownDown"
  >
    <label
      v-if="label"
      class="adn-input__label"
      :class="{
        'adn-input__label--disabled': disabled,
        'adn-input__label--error': errorMessage,
      }"
      :for="inputId"
      data-test-label
    >
      {{ label }}
    </label>
    <div
      class="adn-input__wrapper"
      :class="{
        'adn-input__wrapper--open': showOptions,
        'adn-input__wrapper--disabled': disabled,
        'adn-input__wrapper--error': errorMessage,
        'adn-input__wrapper--clickable': type === 'select',
      }"
      data-test-wrapper
    >
      <div
        class="adn-input__container"
        :class="{
          'adn-input__container--open': showOptions,
          'adn-input__container--disabled': disabled,
        }"
        data-test-container
      >
        <span
          v-if="showIconLeft"
          class="adn-input__icon adn-input__icon-left adn-icon"
          :class="iconLeftClassNames"
          aria-hidden="true"
          data-test-icon-left
          @click.stop="onIconLeftClick"
        />
        <span
          v-if="inputLabel"
          class="adn-input__input adn-input__input-label"
          data-test-input-label-forced
          @submit="onInputSubmit(value)"
          @click="onInputLabelFocus"
        >
          {{ inputLabel }}
        </span>
        <input
          :id="inputId"
          ref="input"
          v-model="displayValue"
          type="text"
          :enterkeyhint="enterkeyhint"
          :readonly="readonly || type === 'file'"
          autocomplete="off"
          autocorrect="off"
          autocapitalize="off"
          spellcheck="false"
          :placeholder="placeholder"
          class="adn-input__input"
          :class="inputClassNames"
          :disabled="disabled"
          @input="onInputInput($event.currentTarget.value)"
          @submit="onInputSubmit(value)"
          @focus="onInputFocus"
        />
        <form v-if="type === 'file'" @submit.prevent="submitFile">
          <!-- https://buzut.net/customisez-le-input-file/ -->
          <input
            ref="fileInput"
            type="file"
            class="adn-input__file-input"
            multiple
            data-test-file-input
            @change="handleFileUpload($event)"
          />
        </form>
        <span
          v-if="showIconClear"
          class="adn-input__icon adn-icon"
          :class="iconClearClassNames"
          aria-hidden="true"
          data-test-icon-clear
          @click.stop="onIconClearClick('clean')"
        />
        <span
          v-if="showIconRight"
          class="adn-input__icon adn-input__icon-right adn-icon"
          :class="iconRightClassNames"
          aria-hidden="true"
          data-test-icon-right
          @click.stop="onIconRightClick"
        />
        <adn-button
          v-show="showButton"
          id="inputButton"
          :kind="kind"
          class="adn-input__button"
          :class="{ 'adn-input__button--large': buttonLarge }"
          :disabled="disabled"
          data-test-button
          @click.stop="onInputSubmit(value)"
        >
          {{ buttonLabel }}
        </adn-button>
        <span
          v-if="type === 'select' && selectIconEnabled"
          class="adn-input__icon adn-icon"
          :class="iconSelectDirectionClassNames"
          aria-hidden="true"
          data-test-icon-dropdown
          @click.stop="onIconSelectDirectionClick"
        />
      </div>
      <div v-show="showOptions" class="adn-input__separator" />
      <div v-show="showOptions" class="adn-input__options">
        <slot name="options-header" :selection="selection" />
        <div class="adn-input__options-scrolling">
          <ul ref="options" class="adn-input__options-list">
            <slot
              name="options-before"
              :selection="selection"
              :preselected-option-index="preselectedOptionIndex"
              :preselect-option="preselectOption"
            />
            <li v-for="(option, index) in adaptedOptions" :key="index">
              <slot
                name="option"
                :option="option"
                :preselected="index === preselectedOptionIndex"
                :selected="isOptionSelected(index)"
                :preselect-option="() => preselectOption(index)"
                :select-option="() => selectOption(index)"
              >
                <adn-select-option
                  class="adn-input__option"
                  :selected="isOptionSelected(index)"
                  :option="option"
                  :preselected="index === preselectedOptionIndex"
                  @focusenter="preselectOption(index)"
                  @click="selectOption(index)"
                  @keyup.enter.stop="selectOption(index)"
                />
              </slot>
            </li>
            <slot
              name="options-after"
              :selection="selection"
              :preselected-option-index="preselectedOptionIndex"
              :preselect-option="preselectOption"
            />
          </ul>
        </div>
        <slot name="options-footer" :selection="selection" />
      </div>
    </div>
    <div v-if="errorMessage" class="adn-input__error" data-test-error>
      <span class="adn-icon adn-icon--status-unavailable" aria-hidden="true" />
      <span>{{ errorMessage }}</span>
    </div>
  </div>
</template>

<script>
// Attention: le passage en TS est très complèxe
import uuidv4 from '@b2ag/utils/src/uuid'
import { defineComponent } from 'vue'
import AdnButton from '../AdnButton/AdnButton.vue'
import AdnSelectOption from './AdnSelectOption.vue'
import AdnSelectOptionProps from './AdnSelectOptionProps'

const defaultOptionIndex = -1

export default defineComponent({
  name: 'AdnInput',
  components: {
    AdnButton,
    AdnSelectOption,
  },
  props: {
    placeholder: {
      type: String,
      default: undefined,
    },
    initialValue: {
      type: String,
      default: undefined,
    },
    validate: {
      type: Function,
      default: () => null,
    },
    validateFile: {
      type: Function,
      default: () => null,
    },
    type: {
      type: String,
      default: 'text',
      validator: (val) => ['text', 'file', 'select'].includes(val),
    },
    label: {
      type: String,
      default: undefined,
    },
    inputLabel: {
      type: String,
      default: undefined,
    },
    readonly: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    leftIconName: {
      type: String,
      default: undefined,
    },
    leftIconClickable: {
      type: Boolean,
      default: false,
    },
    rightIconName: {
      type: String,
      default: undefined,
    },
    rightIconClickable: {
      type: Boolean,
      default: false,
    },
    buttonLabel: {
      type: String,
      default: undefined,
    },
    buttonLarge: {
      type: Boolean,
      default: false,
    },
    kind: {
      type: String,
      default: 'primary',
      validator: (val) => ['primary', 'secondary'].includes(val),
    },
    options: {
      type: Array,
      default: () => [],
    },
    optionsBefore: {
      type: Array,
      default: () => [],
    },
    optionsAfter: {
      type: Array,
      default: () => [],
    },
    extractOptionValue: {
      type: Function,
      default: (identity) => identity,
    },
    multiple: {
      type: Boolean,
      default: false,
    },
    selectIconEnabled: {
      type: Boolean,
      default: true,
    },
    error: {
      type: String,
      default: '',
    },
    editableSelect: {
      type: Boolean,
      default: false,
    },
    shouldShowOptions: {
      type: Function,
      default: undefined,
    },
    enterkeyhint: {
      type: String,
      default: 'enter',
    },
    selectionRange: {
      type: Number,
    },
  },
  emits: [
    'input',
    'focus',
    'options:visible',
    'click',
    'submit',
    'change',
    'keyDownDown',
    'keyDownUp',
    'update:modelValue',
    'iconLeftClick',
    'iconRightClick',
    'cleanClick',
  ],
  data() {
    return {
      inputId: null,
      value: '',
      previousValue: '',
      selection: [],
      selectedIndexes: [],
      file: '',
      validationError: '',
      optionsExpanded: false,
      preselectedOptionIndex: defaultOptionIndex,
    }
  },
  computed: {
    errorMessage() {
      return this.error || this.validationError
    },
    displayValue: {
      get() {
        if (Array.isArray(this.value)) {
          return this.value.join(', ')
        }
        return this.value
      },
      set(modifiedValue) {
        this.value = modifiedValue
      },
    },
    showIconClear() {
      return (
        !this.inputLabel &&
        !this.disabled &&
        !this.readonly &&
        this.type !== 'file' &&
        this.value &&
        this.value.length > 0
      )
    },
    showIconRight() {
      return this.rightIconName && this.rightIconName.length > 0
    },
    showIconLeft() {
      return this.leftIconName && this.leftIconName.length > 0
    },
    showButton() {
      return this.buttonLabel && this.buttonLabel.length > 0
    },
    showOptions() {
      return (
        this.type === 'select' &&
        this.optionsExpanded &&
        ((this.shouldShowOptions === undefined && this.options && this.options.length > 0) ||
          (this.shouldShowOptions instanceof Function && this.shouldShowOptions()))
      )
    },
    iconRightClassNames() {
      const classNames = [`adn-icon--${this.rightIconName}`]
      if (!this.disabled && this.rightIconClickable) {
        classNames.push('adn-input__icon--clickable')
      }
      if (this.disabled) {
        classNames.push('adn-input__icon--disabled')
      }
      if (this.errorMessage) {
        classNames.push('adn-input__icon--error')
      }
      return classNames
    },
    inputClassNames() {
      const classNames = []
      if (this.type === 'select') {
        classNames.push('adn-input__input--clickable')
      }
      if (this.disabled) {
        classNames.push('adn-input__input--disabled')
      }
      if (this.readonly) {
        classNames.push('adn-input__input--readonly')
      }
      if (this.showIconLeft) {
        classNames.push('adn-input__input-left-content')
      }
      if (this.showIconClear || this.showIconRight || this.showButton) {
        classNames.push('adn-input__input-right-content')
      }
      if (this.inputLabel) {
        classNames.push('adn-input__input--hidden')
      }
      return classNames
    },
    iconClearClassNames() {
      const classNames = [`adn-icon--navigation-close`]
      if (!this.disabled) {
        classNames.push('adn-input__icon--clickable')
      }
      return classNames
    },
    iconLeftClassNames() {
      const classNames = [`adn-icon--${this.leftIconName}`]
      if (!this.disabled && this.leftIconClickable) {
        classNames.push('adn-input__icon--clickable')
      }
      if (this.disabled) {
        classNames.push('adn-input__icon--disabled')
      }
      if (this.errorMessage) {
        classNames.push('adn-input__icon--error')
      }
      return classNames
    },
    iconSelectDirectionClassNames() {
      const classNames = [`adn-icon--navigation-direction-${this.optionsExpanded ? 'up' : 'down'}`]
      if (!this.disabled) {
        classNames.push('adn-input__icon--clickable')
      }
      if (this.errorMessage) {
        classNames.push('adn-input__icon--error')
      }
      return classNames
    },
    adaptedOptions() {
      return this.options.map((option) =>
        option instanceof AdnSelectOptionProps
          ? option
          : typeof option === 'string'
            ? new AdnSelectOptionProps({ payload: option })
            : new AdnSelectOptionProps({ ...option }),
      )
    },
  },
  mounted() {
    this.inputId = uuidv4()
    if (this.selectionRange) this.input.setSelectionRange(selectionRange, selectionRange)
  },
  created() {
    document.addEventListener('click', this.onDocumentClick)
    this.value = this.initialValue
    if (this.initialValue && this.type === 'select' && this.multiple) {
      const initialValues = this.initialValue.split(',')

      for (let index = 0; index < this.adaptedOptions.length; index++) {
        const optionValue = this.extractOptionValue(this.adaptedOptions[index].payload)
        if (initialValues.find((initialValue) => initialValue === optionValue)) {
          this.selectedIndexes.push(index)
          this.selection.push(optionValue)
        }
      }
      this.value = this.selection
    }
    this.previousValue = this.value

    this.validationError = this.validate.apply()
    this.preselectedOptionIndex = defaultOptionIndex - this.optionsBefore.length
    this.preselectNextOption()
  },
  unmounted() {
    document.removeEventListener('click', this.onDocumentClick)
  },
  methods: {
    onKeyUpEnter() {
      if (this.options.length && this.optionsExpanded) {
        this.selectOption(this.preselectedOptionIndex)
      } else {
        this.onInputSubmit(this.value)
      }
    },
    onKeyDownUp() {
      if (this.options) {
        if (!this.optionsExpanded) {
          this.revealOptions()
        }
        this.preselectPreviousOption()
      }
      this.emitPayloadAndIndexAndSearchTerm('keyDownUp')
    },
    onKeyDownDown() {
      if (this.options) {
        if (!this.optionsExpanded) {
          this.revealOptions()
        }
        this.preselectNextOption()
      }
      this.emitPayloadAndIndexAndSearchTerm('keyDownDown')
    },
    scrollToPreselectedOption() {
      if (this.$refs.options?.children && this.$refs.options.children.length > 0) {
        if (this.$refs.options.children[this.preselectedOptionIndex + this.optionsBefore.length].scrollIntoView)
          this.$refs.options.children[this.preselectedOptionIndex + this.optionsBefore.length].scrollIntoView({
            block: 'nearest',
            inline: 'nearest',
          })
      }
    },
    preselectPreviousOption() {
      let nextPreselectedOptionIndex = this.preselectedOptionIndex
      if (
        nextPreselectedOptionIndex <= 0 &&
        this.optionsBefore.length > 0 &&
        this.optionsBefore.length + nextPreselectedOptionIndex > 0
      ) {
        nextPreselectedOptionIndex -= 1
        this.preselectedOptionIndex = nextPreselectedOptionIndex
      } else {
        let nextPreselectedOption
        do {
          nextPreselectedOptionIndex -= 1
          if (nextPreselectedOptionIndex >= 0) {
            nextPreselectedOption = this.adaptedOptions[nextPreselectedOptionIndex]
          }
        } while (nextPreselectedOption && nextPreselectedOption.disabled)
        if (nextPreselectedOption) {
          this.preselectedOptionIndex = nextPreselectedOptionIndex
        }
      }
      this.$nextTick(() => this.scrollToPreselectedOption())
    },
    preselectNextOption() {
      let nextPreselectedOptionIndex = this.preselectedOptionIndex
      if (nextPreselectedOptionIndex < defaultOptionIndex || nextPreselectedOptionIndex >= this.adaptedOptions.length) {
        nextPreselectedOptionIndex += 1
        this.preselectedOptionIndex = nextPreselectedOptionIndex
      } else {
        let nextPreselectedOption
        do {
          nextPreselectedOptionIndex += 1
          if (nextPreselectedOptionIndex < this.adaptedOptions.length) {
            nextPreselectedOption = this.adaptedOptions[nextPreselectedOptionIndex]
          }
        } while (nextPreselectedOption && nextPreselectedOption.disabled)
        if (nextPreselectedOption) {
          this.preselectedOptionIndex = nextPreselectedOptionIndex
        }
      }
      this.$nextTick(() => this.scrollToPreselectedOption())
    },
    refreshOptions() {
      if (!this.optionsExpanded && this.options && !this.disabled) {
        if (this.type === 'select') {
          if (!this.editableSelect || (this.$refs.input.value && this.$refs.input.value.length > 0)) {
            this.revealOptions()
          }
        } else {
          this.revealOptions()
        }
      }
    },
    onInputFocus() {
      this.refreshOptions()
      if (this.type === 'text' && this.value && this.value.length) {
        this.$refs.input.setSelectionRange(0, this.value.length)
      }
      this.$emit('focus', this.value)
    },
    onInputLabelFocus() {
      if (!this.optionsExpanded && this.options && !this.disabled) {
        this.revealOptions()
      } else if (this.optionsExpanded) {
        this.hideOptions()
      }
    },
    onInputChange(value) {
      this.$emit('change', value)
      this.previousValue = this.value
    },
    onInputInput(value) {
      this.validationError = this.validate(value)
      this.$emit('input', value)
      this.$emit('update:modelValue', value)
      this.refreshOptions()
    },
    onInputSubmit(value) {
      if (this.type === 'file') {
        this.submitFile()
      } else {
        this.$emit('submit', value)
      }
      this.hideOptions()
    },
    emitPayloadAndIndexAndSearchTerm(event) {
      const searchTerm = this.$refs.input.value
      if (this.preselectedOptionIndex >= 0 && this.preselectedOptionIndex < this.adaptedOptions.length) {
        this.$emit(
          event,
          this.adaptedOptions[this.preselectedOptionIndex].payload,
          this.preselectedOptionIndex,
          searchTerm,
        )
      } else if (this.preselectedOptionIndex < 0) {
        this.$emit(
          event,
          this.optionsBefore[-1 - this.preselectedOptionIndex],
          -1 - this.preselectedOptionIndex,
          searchTerm,
        )
      } else if (this.preselectedOptionIndex >= this.adaptedOptions.length) {
        this.$emit(
          event,
          this.optionsAfter[this.preselectedOptionIndex - this.adaptedOptions.length],
          this.preselectedOptionIndex - this.adaptedOptions.length,
          searchTerm,
        )
      }
    },
    onOptionSelect() {
      if (this.type === 'select') {
        this.emitPayloadAndIndexAndSearchTerm('select')
      }
    },
    onIconLeftClick() {
      if (this.disabled) return
      this.$emit('iconLeftClick', this.value)
      this.$refs.input.focus()
    },
    onIconRightClick() {
      if (this.disabled) return
      this.$emit('iconRightClick', this.value)
    },
    onInputClick() {
      if (this.type === 'file') {
        this.$refs.fileInput.click()
      } else if (this.options && !this.optionsExpanded) {
        this.$refs.input.click()
      }
      this.$emit('click', this.value)
    },
    onIconClearClick(value) {
      this.value = ''
      this.selection = []
      this.$emit('cleanClick', value)
      if (this.type === 'text') {
        this.onInputInput('')
      } else {
        this.onInputChange('')
      }
    },
    handleFileUpload(event) {
      this.file = event.target?.files[0]
      this.validationError = this.validateFile(this.file)
      if (this.file && this.file.name) {
        this.value = this.file.name
      }
    },
    async submitFile() {
      if (!this.validationError) {
        this.$emit('submit', this.file)
      }
    },
    onIconSelectDirectionClick() {
      if (this.optionsExpanded) {
        this.hideOptions()
      } else {
        this.$refs.input.focus()
      }
    },
    revealOptions() {
      if (this.shouldShowOptions === undefined || this.shouldShowOptions()) {
        this.optionsExpanded = true
      }
      this.$emit('options:visible', true)
    },
    hideOptions() {
      this.optionsExpanded = false
      this.$emit('options:visible', false)
      this.$nextTick(() => {
        if (this.type === 'select' && this.multiple === false && this.selectedIndexes.length > 0) {
          this.preselectedOptionIndex = this.selectedIndexes[0]
        }
      })
    },
    onDocumentClick(clickEvent) {
      if (!this.$el.contains(clickEvent.target)) {
        this.hideOptions()
      }
      if (this.previousValue !== this.value) {
        this.onInputChange(this.value)
      }
    },
    preselectOption(index) {
      this.preselectedOptionIndex = index
    },
    isOptionSelected(index) {
      return this.options && this.selectedIndexes.indexOf(index) > -1
    },
    setSelection(selectedValues) {
      this.selection.splice(0, this.selection.length)
      this.selectedIndexes.splice(0, this.selectedIndexes.length)

      for (let index = 0; index < this.adaptedOptions.length; index++) {
        const optionValue = this.extractOptionValue(this.adaptedOptions[index].payload)
        if (selectedValues.find((selectedValue) => selectedValue === optionValue)) {
          this.selectedIndexes.push(index)
          this.selection.push(optionValue)
        }
      }
    },
    selectOption(index, withUpOrDownKeyboard = false) {
      if (index >= 0 && index <= this.adaptedOptions.length) {
        const value = this.extractOptionValue(this.adaptedOptions[index].payload)
        const valueIndex = this.selection.indexOf(value)
        if (this.multiple) {
          if (valueIndex === -1) {
            this.selectedIndexes.push(index)
            this.selection.push(value)
          } else {
            this.selectedIndexes.splice(this.selectedIndexes.indexOf(index), 1)
            this.selection.splice(valueIndex, 1)
          }
          const orderedSelection = []
          this.adaptedOptions.forEach((adaptedOption) => {
            if (this.selection.find((selected) => selected === this.extractOptionValue(adaptedOption.payload))) {
              orderedSelection.push(this.extractOptionValue(adaptedOption.payload))
            }
          })
          this.selection = orderedSelection
          this.value = orderedSelection
        } else {
          this.selectedIndexes = [index]
          this.value = value
        }
        this.onInputChange(this.value)
      }
      if (!withUpOrDownKeyboard && !this.multiple) {
        this.hideOptions()
        this.onOptionSelect()
      }
    },
  },
})
</script>

<style scoped lang="scss">
.adn-input {
  position: relative;
  max-width: 100%;
  height: 4.4rem;

  &--labelled {
    height: 6.6rem;
  }

  &__label {
    display: block;
    text-align: left;
    height: 2.2rem;
    margin-bottom: 0;
    text-overflow: ellipsis;
    white-space: nowrap;
    overflow: hidden;

    &--disabled {
      color: var(--color-inactive-light);
    }

    &--error {
      color: var(--color-error);
    }
  }

  &__wrapper {
    max-width: inherit;
    transition: all 0.2s;
    overflow: hidden;
    box-shadow: none;
    text-align: center;
    color: var(--color-text);
    background-color: var(--color-background-light);
    border-radius: var(--border-radius-1);
    border: 1px solid var(--color-text);

    &:hover {
      box-shadow: 0 4px 4px rgb(0 0 0 / 25%);
    }

    &:focus-within {
      box-shadow: 0 4px 4px rgb(0 0 0 / 25%);
    }

    &--open {
      box-shadow: 0 4px 4px rgb(0 0 0 / 25%);
      border-radius: var(--border-radius-1) var(--border-radius-1) 0 0;
    }

    &--disabled {
      box-shadow: none;
      border-color: var(--color-inactive-light);
      color: var(--color-inactive-light);

      &:hover {
        box-shadow: none;
      }
    }

    &--error {
      border-color: var(--color-error);
    }

    &--clickable {
      cursor: pointer;
    }
  }

  &__container {
    margin-top: -1px;
    margin-bottom: -1px;
    display: flex;
    flex-direction: row;
    justify-content: space-between;

    &--disabled {
      cursor: not-allowed;
    }
  }

  &__icon {
    display: inline-block;
    width: 4.4rem;
    min-width: 4.4rem;
    height: 4.4rem;
    line-height: 4.4rem;
    text-align: center;

    &-left {
      flex-grow: 0;
    }

    &-right {
      flex-grow: 0;
    }

    &--error {
      color: var(--color-error);
    }

    &--disabled {
      cursor: not-allowed;
    }

    &--clickable {
      cursor: pointer;
    }

    + .adn-input__icon {
      margin-left: -1rem;
    }
  }

  &__input {
    flex-grow: 1;
    display: block;
    width: 100%;
    height: 4.4rem;
    line-height: 2rem;
    vertical-align: middle;
    border-radius: 0;
    border: none;
    font-size: 1em;
    color: var(--color-text);
    padding: 12px 0;
    outline: none;
    margin: auto 2rem auto 1rem;
    background-color: var(--color-background-light);
    box-sizing: border-box;

    &-left-content {
      margin-left: 0;
    }

    &-right-content {
      margin-right: 0;
    }

    &-label {
      text-align: left;
    }

    &--clickable {
      cursor: pointer;
    }

    &--disabled {
      cursor: not-allowed;
    }

    &--hidden {
      position: absolute;
      height: 1px;
      width: 1px;
      z-index: -1;
    }

    &--readonly {
      cursor: default;
    }

    &:focus-within {
      border: none;
    }

    &:focus {
      border: none;
    }

    &::-ms-clear {
      display: none;
    }

    &:placeholder-shown {
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    &::placeholder {
      font-size: 1em;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      text-align: left;
      color: var(--color-text-light);
    }

    &--disabled {
      color: var(--color-inactive-light);

      &::placeholder {
        color: var(--color-inactive-light);
      }
    }
  }

  &__file-input {
    position: absolute;
    height: 1px;
    width: 1px;
    z-index: -1;
  }

  &__button {
    flex-grow: 0;
    margin: 7px 5px;
    padding: 0 8px;
    min-height: unset;
    line-height: 30px;
    height: 30px;
    min-width: 92px;
    font-size: var(--font-size-s);
    user-select: none;

    &--large {
      margin: 0;
      height: 4.4rem;
      line-height: 4.4rem;
      min-width: 130px;
      width: 130px;
    }
  }

  &__separator {
    margin: 1px 10px 0;
    height: 1px;
    background-color: var(--color-inactive-dark);
  }

  &__options {
    width: 100%;
    max-width: inherit;
    overflow-x: hidden;
    position: absolute;
    left: 0;
    border: 1px solid var(--color-text);
    border-top: 0 none;
    border-radius: 0 0 var(--border-radius-1) var(--border-radius-1);
    z-index: 2;
    box-shadow: 0 4px 4px rgb(0 0 0 / 25%);
    background-color: var(--color-background-light);
    cursor: default;

    &-scrolling {
      height: auto;
      max-height: 5 * 4.4rem;
      overflow-y: scroll;
    }

    &-list {
      position: relative;
    }
  }

  &__error {
    color: var(--color-error);
    font-weight: bold;
  }
}
</style>
